import { Checkbox } from "@/components/shadcn/checkbox";
import {
    Field,
    FieldContent,
    FieldError,
    FieldLabel,
} from "@/components/shadcn/field";
import { cn } from "cn";
import type { ReactNode } from "react";
import {
    Controller,
    FieldPath,
    FieldValues,
    type Control,
} from "react-hook-form";

type CheckboxFormFieldProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
    control: Control<TFieldValues>;
    name: TName;
    label: ReactNode;
    disabled?: boolean;
    className?: string;
    labelClassName?: string;
};

export function CheckboxFormField<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
    control,
    name,
    label,
    disabled = false,
    className,
    labelClassName,
}: CheckboxFormFieldProps<TFieldValues, TName>) {
    return (
        <Controller
            control={control}
            name={name}
            render={({ field, fieldState }) => (
                <Field
                    orientation="horizontal"
                    data-invalid={fieldState.invalid}
                    data-disabled={disabled}
                    className={cn("items-start gap-3", className)}
                >
                    <Checkbox
                        id={field.name}
                        checked={field.value === true}
                        onCheckedChange={(checked) =>
                            field.onChange(checked === true)
                        }
                        disabled={disabled}
                        aria-invalid={fieldState.invalid}
                    />
                    <FieldContent className="gap-1">
                        <FieldLabel
                            htmlFor={field.name}
                            className={cn("font-normal", labelClassName)}
                        >
                            {label}
                        </FieldLabel>
                        {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                        )}
                    </FieldContent>
                </Field>
            )}
        />
    );
}
